import React, { useEffect, useRef, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { IonContent, IonPage, IonSpinner } from '@ionic/react';
import { Storage } from '@capacitor/storage';
import { auth } from '../config/firebase.config';
import { USER_STORAGE_KEYS } from '../constants/storage';
import FavoritesService from '../services/FavoritesService';
import FirebaseTokenService from '../services/FirebaseTokenService';
import NetworkingService from '../services/NetworkingService';
import { emailPasswordAuthenticationService } from '../services/EmailPasswordAuthenticationService';

const CLEANUP_TIMEOUT_MS = 5000;

interface RedirectToLoginProps {
  setIsLoggedIn: Function;
  setUsername: Function;
}

const RedirectToLogin: React.FC<RedirectToLoginProps> = ({ setIsLoggedIn, setUsername }) => {
  const logoutStarted = useRef(false);
  const [logoutComplete, setLogoutComplete] = useState(false);
  
  useEffect(() => {
    if (logoutStarted.current) return;
    logoutStarted.current = true;

    let isMounted = true;

    const runSafely = async (label: string, task: () => Promise<unknown>): Promise<void> => {
      let timeoutId: number | undefined;

      try {
        await Promise.race([
          task(),
          new Promise<void>(resolve => {
            timeoutId = window.setTimeout(() => {
              console.warn(`La limpieza de ${label} excedió el tiempo máximo; continuando con el cierre.`);
              resolve();
            }, CLEANUP_TIMEOUT_MS);
          })
        ]);
      } catch (error) {
        console.warn(`No se pudo completar la limpieza de ${label}:`, error);
      } finally {
        if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      }
    };

    const logout = async () => {
      // Detener primero los procesos que todavía podrían usar la sesión anterior.
      FavoritesService.getInstance().cleanup();
      emailPasswordAuthenticationService.resetRuntimeState();

      const menu = document.querySelector('ion-menu') as HTMLIonMenuElement | null;

      await Promise.all([
        runSafely('menú', async () => { await menu?.close(); }),
        runSafely('Firebase Auth', async () => {
          if (auth.currentUser) await auth.signOut();
        }),
        runSafely('token Firebase', () => FirebaseTokenService.getInstance().clearToken()),
        runSafely('perfil local de networking', () => NetworkingService.getInstance().clearLocalData()),
        runSafely('estado de acceso', () => Storage.set({
          key: USER_STORAGE_KEYS.HAS_LOGGED_IN,
          value: JSON.stringify(false)
        })),
        ...[
          USER_STORAGE_KEYS.USERNAME,
          USER_STORAGE_KEYS.USER_ID,
          USER_STORAGE_KEYS.USER_OTHER,
          USER_STORAGE_KEYS.USER_CED
        ].map(key => runSafely(key, () => Storage.remove({ key })))
      ]);

      await Promise.all([
        runSafely('estado global de acceso', () => Promise.resolve(setIsLoggedIn(false))),
        runSafely('usuario global', () => Promise.resolve(setUsername(undefined)))
      ]);

      if (isMounted) setLogoutComplete(true);
    };

    void logout();

    return () => {
      isMounted = false;
    };
  }, [setIsLoggedIn, setUsername]);

  return (
    <IonPage>
      {logoutComplete && <Redirect to="/login" />}
      <IonContent>
        <div className="ion-text-center ion-padding">
          <IonSpinner name="crescent" />
        </div>
      </IonContent>
    </IonPage>
  );
};

export default RedirectToLogin;
